import React, { useEffect, useState, useRef } from 'react';
import { AlertTriangle, X, MapPin } from 'lucide-react';
import { startPoll } from '../lib/poll';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000;  // v2.6.4: was 30 s

// Browser-side beep generator (no asset file needed — Web Audio API).
function playBeep(times = 3) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    for (let i = 0; i < times; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0, now + i * 0.45);
      gain.gain.linearRampToValueAtTime(0.4, now + i * 0.45 + 0.05);
      gain.gain.linearRampToValueAtTime(0, now + i * 0.45 + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.45);
      osc.stop(now + i * 0.45 + 0.4);
    }
    setTimeout(() => ctx.close().catch(() => {}), 2500);
  } catch {}
}

/**
 * Polls /api/alerts/unread every 30s. Plays a beep + shows toast on new ones.
 * Used by managers (any user with reports). Mounted globally on staff/admin shells.
 */
export default function AlertWatcher() {
  const [alerts, setAlerts] = useState([]);
  const seenRef = useRef(new Set());
  const dismissedRef = useRef(new Set()); // ids locally hidden — never show again

  useEffect(() => {
    let stopped = false;

    const tick = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const r = await fetch(`${API_URL}/api/alerts/unread`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (!r.ok) return;
        const rows = await r.json();
        if (stopped) return;
        // Drop anything the user already clicked X on — defends against a
        // race where the backend hasn't flipped `read=true` yet.
        const visible = rows.filter(a => !dismissedRef.current.has(a.id));
        const fresh = visible.filter(a => !seenRef.current.has(a.id));
        if (fresh.length > 0) {
          fresh.forEach(a => seenRef.current.add(a.id));
          if (fresh.some(a => a.play_sound)) playBeep(3);
        }
        setAlerts(visible);
      } catch {}
    };

    const stop = startPoll(tick, POLL_INTERVAL_MS);
    return () => { stopped = true; stop(); };
  }, []);

  const dismiss = async (alertId) => {
    // 1) Hide immediately — user clicked X, they expect it to disappear.
    dismissedRef.current.add(alertId);
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    // 2) Tell the backend (fire-and-forget; the local dismissedRef guards
    //    against the poll re-surfacing it even if this fetch fails).
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch {}
  };

  if (alerts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[60] space-y-2 w-[min(360px,calc(100vw-2rem))]" data-testid="alert-watcher">
      {alerts.slice(0, 3).map(a => (
        <div
          key={a.id}
          className="bg-white border-l-4 border-red-500 shadow-lg rounded-lg p-3 animate-pulse-once"
          data-testid={`alert-${a.id}`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-red-900">{a.title || 'Alert'}</p>
              <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-line">{a.body}</p>
              {a.data?.map_link && (
                <a
                  href={a.data.map_link}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline mt-1"
                  data-testid={`alert-${a.id}-map`}
                >
                  <MapPin size={11} /> Open map
                </a>
              )}
            </div>
            <button
              onClick={() => dismiss(a.id)}
              className="p-1 text-gray-400 hover:text-gray-700 rounded"
              data-testid={`alert-${a.id}-dismiss`}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
      {alerts.length > 3 && (
        <p className="text-xs text-center text-gray-500 bg-white/80 rounded py-1">
          + {alerts.length - 3} more alerts
        </p>
      )}
    </div>
  );
}
